import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

export interface ICrewSoundPlayer {
  /**
   * 音声ファイルを再生する
   * @param requester 再生を要求したコンポーネントを識別するID
   * @param start 再生開始位置(秒)
   * @param end 再生終了位置(秒)。nullの場合は最後まで再生する
   */
  play(requester: string, start: number, end: number | null): void
  /**
   * 音声ファイルの再生を停止する
   */
  stop(): void
}

const State = {
  loading: 'loading',
  playing: 'playing',
  stopped: 'stopped',
} as const
// eslint-disable-next-line @typescript-eslint/no-redeclare
type State = (typeof State)[keyof typeof State]

export type SoundStatus = {
  // 現在の再生状態
  state: State
  // 全体の長さ
  durationSec: number | null

  // 現在の再生を開始したリクエスタのID
  currentRequester: string | null
  // 現在の再生位置
  playbackPositionSec: number
  // 現在の再生の終了予定位置
  playbackEndSec: number | null
}

type Props = {
  url: string
  onChangeStatus?: (status: SoundStatus) => void
}

export const CrewSoundPlayer = memo(
  forwardRef<ICrewSoundPlayer, Props>((props, ref) => {
    const audioRef = useRef<HTMLAudioElement>(null)

    const [status, setStatus] = useState<SoundStatus>({
      state: State.loading,
      durationSec: null,
      currentRequester: null,
      playbackPositionSec: 0,
      playbackEndSec: null,
    })

    // statusの変更を通知する
    useEffect(() => {
      props.onChangeStatus?.(status)
    }, [status, props.onChangeStatus, props])

    // 再生開始。refで外部に公開し、外部から呼び出す
    const play = useCallback(
      (requester: string, start: number, end: number | null) => {
        if (!audioRef.current || status.state === State.loading) {
          return
        }

        audioRef.current.currentTime = start
        audioRef.current.play()

        setStatus((prevStatus) => ({
          ...prevStatus,
          state: State.playing,
          currentRequester: requester,
          playbackPositionSec: start,
          playbackEndSec: end,
        }))
      },
      [status.state]
    )

    // 再生停止。refで外部に公開し、外部から呼び出す
    const stop = useCallback(() => {
      if (!audioRef.current || status.state === State.loading) {
        return
      }
      audioRef.current?.pause()
      setStatus((prevStatus) => ({
        ...prevStatus,
        state: State.stopped,
        currentRequester: null,
      }))
    }, [status.state])

    // playとstopをrefに登録し、外側のコンポーネントからメソッドとして呼び出せるようにする
    useImperativeHandle(
      ref,
      () => ({
        play,
        stop,
      }),
      [play, stop]
    )

    useLayoutEffect(() => {
      // イベントハンドラ初期化
      // 再生進行している
      const handleTimeUpdate = () => {
        const audio = audioRef.current
        if (!audio) {
          return
        }

        // 指定時間まで再生したら停止する
        if (
          status.playbackEndSec &&
          audio.currentTime >= status.playbackEndSec
        ) {
          audio.pause()
          setStatus((prevStatus) => ({
            ...prevStatus,
            state: State.stopped,
            currentRequester: null,
          }))
          return
        }

        // 指定時間まで再生が終わっていない、時刻更新
        setStatus((prevStatus) => ({
          ...prevStatus,
          playbackPositionSec: audio.currentTime,
        }))
      }

      // 再生可能になった
      const handleCanPlay = () => {
        setStatus((prevStatus) => ({
          ...prevStatus,
          // 準備完了
          state:
            //ロード中だったら再生可能に変更する。このイベントは再生途中でも発火するため条件分岐が必要。
            prevStatus.state === State.loading
              ? State.stopped
              : prevStatus.state,
        }))
      }

      // 最後まで再生した
      const handleEnded = () => {
        setStatus((prevStatus) => ({
          ...prevStatus,
          state: State.stopped,
          currentRequester: null,
        }))
      }

      // 一時停止した
      const handlePause = () => {
        setStatus((prevStatus) => ({
          ...prevStatus,
          // 停止扱いする
          state: State.stopped,
          currentRequester: null,
        }))
      }

      // 再生開始
      const handlePlay = () => {
        setStatus((prevStatus) => ({
          ...prevStatus,
          state: State.playing,
        }))
      }

      const audio = audioRef.current
      audio?.addEventListener('timeupdate', handleTimeUpdate)
      audio?.addEventListener('canplay', handleCanPlay)
      audio?.addEventListener('ended', handleEnded)
      audio?.addEventListener('pause', handlePause)
      audio?.addEventListener('play', handlePlay)

      return () => {
        audio?.removeEventListener('timeupdate', handleTimeUpdate)
        audio?.removeEventListener('canplay', handleCanPlay)
        audio?.removeEventListener('ended', handleEnded)
        audio?.removeEventListener('pause', handlePause)
        audio?.removeEventListener('play', handlePlay)
      }
    }, [audioRef, setStatus, status.durationSec, status.playbackEndSec])

    return <audio ref={audioRef} src={props.url} />
  })
)
